import { useState, useRef, useMemo } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Html } from '@react-three/drei';
import * as THREE from 'three';
import type { TruckResult, TruckItem, TruckSpec } from '@/api/truck-loader';

interface TruckSceneProps {
  truck: TruckResult;
  truckSpec: TruckSpec;
  colorMode: 'type' | 'area' | 'phase';
  areaNames: string[];
  phaseNames: string[];
}

const TYPE_COLORS: Record<string, string> = {
  base: '#3b82f6',
  upper: '#22c55e',
  tall: '#f97316',
};

function goldenAngleColor(index: number, count: number): string {
  const hue = (index * 137.508) % 360;
  return `hsl(${hue}, 70%, 55%)`;
}

function getItemColor(
  item: TruckItem,
  colorMode: string,
  areaNames: string[],
  phaseNames: string[],
): string {
  if (colorMode === 'type') return TYPE_COLORS[item.type] || '#888';
  if (colorMode === 'area') {
    const idx = areaNames.indexOf(item.areaName);
    return goldenAngleColor(idx >= 0 ? idx : 0, areaNames.length);
  }
  const idx = phaseNames.indexOf(item.phaseName);
  return goldenAngleColor(idx >= 0 ? idx : 0, phaseNames.length);
}

function CabinetBox({
  item, color, selected, hovered, onHover, onClick,
}: {
  item: TruckItem;
  color: string;
  selected: boolean;
  hovered: boolean;
  onHover: (item: TruckItem | null) => void;
  onClick: (item: TruckItem) => void;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const c = useMemo(() => new THREE.Color(color), [color]);

  return (
    <mesh
      ref={meshRef}
      position={[
        item.x + item.width / 2,
        item.y + item.height / 2,
        item.z + item.depth / 2,
      ]}
      onPointerOver={(e) => { e.stopPropagation(); onHover(item); }}
      onPointerOut={() => onHover(null)}
      onClick={(e) => { e.stopPropagation(); onClick(item); }}
    >
      <boxGeometry args={[item.width, item.height, item.depth]} />
      <meshStandardMaterial
        color={c}
        roughness={0.25}
        emissive={selected || hovered ? c : new THREE.Color('#000')}
        emissiveIntensity={selected ? 0.4 : hovered ? 0.2 : 0}
        transparent
        opacity={0.85}
      />
    </mesh>
  );
}

function TruckBed({ spec }: { spec: TruckSpec }) {
  const geo = useMemo(
    () => new THREE.EdgesGeometry(new THREE.BoxGeometry(spec.width, spec.height, spec.length)),
    [spec],
  );
  return (
    <lineSegments
      geometry={geo}
      position={[spec.width / 2, spec.height / 2, spec.length / 2]}
    >
      <lineBasicMaterial color="#555" linewidth={1} />
    </lineSegments>
  );
}

export default function TruckScene({
  truck, truckSpec, colorMode, areaNames, phaseNames,
}: TruckSceneProps) {
  const [hoveredItem, setHoveredItem] = useState<TruckItem | null>(null);
  const [selectedItem, setSelectedItem] = useState<TruckItem | null>(null);

  const camPos: [number, number, number] = [
    truckSpec.width * 1.5,
    truckSpec.height * 1.2,
    truckSpec.length * 0.8,
  ];

  return (
    <div style={{ width: '100%', height: 350, position: 'relative' }}>
      <Canvas camera={{ position: camPos, fov: 50 }}>
        <ambientLight intensity={0.6} />
        <directionalLight position={[100, 200, 100]} intensity={0.8} />
        <pointLight position={[-100, 100, -100]} intensity={0.3} />

        <TruckBed spec={truckSpec} />

        {truck.items.map((item, i) => (
          <CabinetBox
            key={`${item.id}-${item.instance}-${i}`}
            item={item}
            color={getItemColor(item, colorMode, areaNames, phaseNames)}
            selected={selectedItem?.id === item.id && selectedItem?.instance === item.instance}
            hovered={hoveredItem?.id === item.id && hoveredItem?.instance === item.instance}
            onHover={setHoveredItem}
            onClick={setSelectedItem}
          />
        ))}

        <OrbitControls
          target={[truckSpec.width / 2, truckSpec.height / 2, truckSpec.length / 2]}
        />

        {hoveredItem && (
          <Html
            position={[
              hoveredItem.x + hoveredItem.width / 2,
              hoveredItem.y + hoveredItem.height + 5,
              hoveredItem.z + hoveredItem.depth / 2,
            ]}
            style={{ pointerEvents: 'none' }}
          >
            <div className="rounded bg-default-900 px-2 py-1 text-xs text-white shadow-lg whitespace-nowrap">
              <div className="font-semibold">{hoveredItem.description}</div>
              <div>{hoveredItem.areaName}</div>
              <div>{hoveredItem.width}"W x {hoveredItem.height}"H x {hoveredItem.depth}"D</div>
              <div>{hoveredItem.weight.toFixed(1)} lbs</div>
            </div>
          </Html>
        )}
      </Canvas>
    </div>
  );
}
